import {
    Button,
    Checkbox,
    FormControlLabel,
    Grid,
    IconButton,
    makeStyles,
    Snackbar,
    Typography,
} from '@material-ui/core';
import {Alert} from '@material-ui/lab';
import CloseIcon from '@material-ui/icons/Close';
import {observer} from 'mobx-react-lite';
import React, {useCallback, useEffect, useState} from 'react';
import {Approval} from '../../../Core/mobx/ParameterStore/Approval';
import {fieldOptions} from '../../../Core/mobx/ParameterStore/ApprovalXref';
import {Parameter} from '../../../Core/mobx/ParameterStore/ParameterStore';
import {TextBox} from '../../../Core/UI';
import {SelectInputElement} from '../../PreQualification/Components/SelectInputElement';
import {CategoryCard} from '../CategoryCard';
import {useBankVerification} from '@/Agent/Common/useBankVerification';
import RootStore from '@/Core/mobx/RootStore';
import {User} from '@/Auth/User';

const useStyles = makeStyles((theme) => ({
    formLabel: {
        color: '#000',
        lineHeight: 1.4,
        '&.Mui-focused': {
            color: '#000',
        },
    },
    dialog: {
        maxWidth: 800,
        padding: '24px 32px',
    },
    dialogContentText: {
        fontSize: '1rem',
        lineHeight: 1.4,
        color: '#555',
        '& strong': {
            fontWeight: 600,
            color: '#000',
        },
    },
    dialogActions: {
        justifyContent: 'start',
        padding: '8px 24px',
    },
    dialogTitle: {
        '& h2': {
            fontWeight: 600,
            fontSize: '1.6rem',
        },
    },
    validateButton: {
        marginTop: theme.spacing(1),
        marginBottom: theme.spacing(2),
    },
    validationStatus: {
        color: '#4caf50',
        fontSize: '0.875rem',
        marginTop: theme.spacing(1),
    },
    validationError: {
        color: '#f44336',
        fontSize: '0.875rem',
        marginTop: theme.spacing(1),
    },
    sectionHeader: {
        display: 'flex',
        alignItems: 'center',
        gap: theme.spacing(1),
    },
}));

type ConditionalCoverageBankingProps = {
    valueChanged: (p?: Parameter, value?: any) => void;
    parameters: Approval;
    setCategory: (entry: string) => void;
    index: number;
};

const ConditionalCoverageBanking = observer(
    ({valueChanged, parameters, setCategory, index}: ConditionalCoverageBankingProps) => {
        const {parameterStore} = RootStore.agentPortalStore;
        const classes = useStyles();
        const {resetVerification, verifyBankAccount} = useBankVerification();

        // Primary account validation state
        const [primaryValidationStatus, setPrimaryValidationStatus] = useState<{
            status: 'pass' | 'fail' | null;
            date?: string;
            agentName?: string;
            error?: string;
        }>({status: null});
        const [isPrimaryValidating, setIsPrimaryValidating] = useState(false);

        // Secondary account validation state
        const [secondaryValidationStatus, setSecondaryValidationStatus] = useState<{
            status: 'pass' | 'fail' | null;
            date?: string;
            agentName?: string;
            error?: string;
        }>({status: null});
        const [isSecondaryValidating, setIsSecondaryValidating] = useState(false);

        // Toast state
        const [toastOpen, setToastOpen] = useState(false);
        const [toastMessage, setToastMessage] = useState('');
        const required = (value = '', additionalCondition = true) => {
            return additionalCondition ? (
                <span>
                    {value} <span style={{color: 'red'}}>*</span>
                </span>
            ) : (
                value
            );
        };

        const filterDraftDateOptions = (options: string[], isFGDraftDate: boolean) => {
            return isFGDraftDate ? options.filter((option) => option !== '14' && option !== '15') : options;
        };

        const isFGDraftDateInvalid =
            (parameters.agentProducts.value.includes('FGPathsetterIUL') ||
                parameters.agentProducts.value.includes('FGEverlast')) &&
            ['14', '15'].includes(parameters.conditionalDraftDate.value);

        const isFGDraftDate =
            (parameters?.agentProducts.value.includes('FGPathsetterIUL') ||
                parameters?.agentProducts.value.includes('FGEverlast')) &&
            !['14', '15'].includes(parameters?.conditionalDraftDate.value);

        // Get agent name from User.Info
        const getAgentName = () => {
            return User.Info?.name || 'Agent';
        };

        // Validate primary account
        const validatePrimaryAccount = async () => {
            // Save form in background before validation
            RootStore.agentPortalStore.contactsFormStore.saveForm('preapproval');

            if (!parameters.conditionalRoutingNumber?.value || !parameters.conditionalAccountNumber?.value) {
                setPrimaryValidationStatus({
                    status: 'fail',
                    error: 'Routing number and account number are required',
                });
                return;
            }

            setIsPrimaryValidating(true);
            setPrimaryValidationStatus({status: null});

            try {
                // Convert account type to numeric (1 for checking, 2 for savings)
                const accountTypeNumeric = parameters.conditionalAccountType?.value === 'SAVINGS' ? 2 : 1;

                const response = await verifyBankAccount({
                    routingNumber: parameters.conditionalRoutingNumber.value,
                    accountNumber: parameters.conditionalAccountNumber.value,
                    accountType: accountTypeNumeric,
                    customerEmail: parameterStore.contact || parameters.emailAddress.value,
                    formId: parameterStore.formid,
                });

                if (response.success && response.result && response.result.state === 'PASS') {
                    const validationDate = new Date().toLocaleDateString('en-US', {
                        month: '2-digit',
                        day: '2-digit',
                        year: 'numeric',
                    });
                    const agentName = getAgentName();
                    setPrimaryValidationStatus({
                        status: 'pass',
                        date: validationDate,
                        agentName: agentName,
                    });

                    // Save validation metadata to parameters so it's persisted when form is saved
                    valueChanged(parameters.primaryAccountValidationDate, validationDate);
                    valueChanged(parameters.primaryAccountValidationAgent, agentName);
                    valueChanged(parameters.primaryAccountValidationError, ''); // Clear any previous error

                    // Save form after successful validation
                    RootStore.agentPortalStore.contactsFormStore.saveForm('preapproval');

                    setToastMessage('Your banking info has been validated.');
                    setToastOpen(true);
                } else {
                    // Use the specific failure message from the API if available
                    let errorMessage = 'Bank verification failed. Please check the routing and account numbers.';

                    if (response.result?.failureMessage) {
                        errorMessage = response.result.failureMessage;
                    } else if (response.result?.state === 'PREVENT_NEXT_WITH_OVERRIDE') {
                        errorMessage =
                            'Bank verification failed. The customer may override if they confirm the information is correct.';
                    }

                    setPrimaryValidationStatus({
                        status: 'fail',
                        error: errorMessage,
                    });

                    // Save error to parameters so it persists
                    valueChanged(parameters.primaryAccountValidationError, errorMessage);
                    // Clear success metadata when validation fails
                    valueChanged(parameters.primaryAccountValidationDate, '');
                    valueChanged(parameters.primaryAccountValidationAgent, '');

                    // Save form after failed validation to persist error state
                    RootStore.agentPortalStore.contactsFormStore.saveForm('preapproval');
                }
            } catch (error) {
                setPrimaryValidationStatus({
                    status: 'fail',
                    error: 'Failed to validate bank account. Please try again.',
                });
            } finally {
                setIsPrimaryValidating(false);
            }
        };

        // Validate secondary account
        const validateSecondaryAccount = async () => {
            // Save form in background before validation
            RootStore.agentPortalStore.contactsFormStore.saveForm('preapproval');

            if (!parameters.secondaryRoutingNumber?.value || !parameters.secondaryAccountNumber?.value) {
                setSecondaryValidationStatus({
                    status: 'fail',
                    error: 'Secondary routing number and account number are required',
                });
                return;
            }

            setIsSecondaryValidating(true);
            setSecondaryValidationStatus({status: null});

            try {
                // Default to checking (1) for secondary account
                const accountTypeNumeric = 1;

                const response = await verifyBankAccount({
                    routingNumber: parameters.secondaryRoutingNumber.value,
                    accountNumber: parameters.secondaryAccountNumber.value,
                    accountType: accountTypeNumeric,
                    customerEmail: parameterStore.contact || parameters.emailAddress.value,
                    formId: parameterStore.formid,
                    policyNumber: parameters.policyNumber?.value,
                });

                if (response.success && response.result && response.result.state === 'PASS') {
                    const validationDate = new Date().toLocaleDateString('en-US', {
                        month: '2-digit',
                        day: '2-digit',
                        year: 'numeric',
                    });
                    const agentName = getAgentName();
                    setSecondaryValidationStatus({
                        status: 'pass',
                        date: validationDate,
                        agentName: agentName,
                    });

                    // Save validation metadata to parameters so it's persisted when form is saved
                    valueChanged(parameters.secondaryAccountValidationDate, validationDate);
                    valueChanged(parameters.secondaryAccountValidationAgent, agentName);
                    valueChanged(parameters.secondaryAccountValidationError, ''); // Clear any previous error

                    // Save form after successful validation
                    RootStore.agentPortalStore.contactsFormStore.saveForm('preapproval');

                    setToastMessage('Your secondary banking info has been validated.');
                    setToastOpen(true);
                } else {
                    // Use the specific failure message from the API if available
                    let errorMessage = 'Bank verification failed. Please check the routing and account numbers.';

                    if (response.result?.failureMessage) {
                        errorMessage = response.result.failureMessage;
                    } else if (response.result?.state === 'PREVENT_NEXT_WITH_OVERRIDE') {
                        errorMessage =
                            'Bank verification failed. The customer may override if they confirm the information is correct.';
                    }

                    setSecondaryValidationStatus({
                        status: 'fail',
                        error: errorMessage,
                    });

                    // Save error to parameters so it persists
                    valueChanged(parameters.secondaryAccountValidationError, errorMessage);
                    // Clear success metadata when validation fails
                    valueChanged(parameters.secondaryAccountValidationDate, '');
                    valueChanged(parameters.secondaryAccountValidationAgent, '');

                    // Save form after failed validation to persist error state
                    RootStore.agentPortalStore.contactsFormStore.saveForm('preapproval');
                }
            } catch (error) {
                setSecondaryValidationStatus({
                    status: 'fail',
                    error: 'Failed to validate bank account. Please try again.',
                });
            } finally {
                setIsSecondaryValidating(false);
            }
        };

        // Load saved validation status on mount
        useEffect(() => {
            // Load primary account validation status
            if (parameters.primaryAccountValidationDate?.value && parameters.primaryAccountValidationAgent?.value) {
                setPrimaryValidationStatus({
                    status: 'pass',
                    date: parameters.primaryAccountValidationDate.value,
                    agentName: parameters.primaryAccountValidationAgent.value,
                });
            } else if (parameters.primaryAccountValidationError?.value) {
                setPrimaryValidationStatus({
                    status: 'fail',
                    error: parameters.primaryAccountValidationError.value,
                });
            }

            // Load secondary account validation status
            if (parameters.secondaryAccountValidationDate?.value && parameters.secondaryAccountValidationAgent?.value) {
                setSecondaryValidationStatus({
                    status: 'pass',
                    date: parameters.secondaryAccountValidationDate.value,
                    agentName: parameters.secondaryAccountValidationAgent.value,
                });
            } else if (parameters.secondaryAccountValidationError?.value) {
                setSecondaryValidationStatus({
                    status: 'fail',
                    error: parameters.secondaryAccountValidationError.value,
                });
            }
        }, []); // Only run on mount

        return (
            <>
                <CategoryCard title={'Banking Information'} setCategory={setCategory} index={index}>
                    <Grid item>
                        <Typography>
                            Please read these disclosures verbatim depending upon draft date selected.
                        </Typography>
                        <Typography style={{marginTop: 16}}>
                            <Typography component="span" style={{fontWeight: 'bold'}}>
                                Next Available – Draft at Approval
                            </Typography>{' '}
                            "Your first payment will be taken right after your policy is approved, and your coverage
                            will start right away. Future payments will come out on your policy start date. If this form
                            is turned in after your policy is issued, the policy date may change, and more than one
                            draft could happen close together."
                        </Typography>
                        <Typography style={{marginTop: 16}}>
                            <Typography component="span" style={{fontWeight: 'bold'}}>
                                Draft on Specific Day – Draft on a Specified Day
                            </Typography>{' '}
                            "Your first payment will be taken on the day of the month you choose (1st–13th or
                            16th–28th). Your coverage will not start until that date. Future payments will always come
                            out on the same day you select. If this form is turned in after your policy is issued, the
                            policy date will change to match the next chosen draft date."
                        </Typography>
                    </Grid>
                    <Grid item xs={4}>
                        <TextBox
                            id="conditionalBankName"
                            label={'What is your bank name?'}
                            value={parameters.conditionalBankName?.value}
                            onChange={(value) => valueChanged(parameters.conditionalBankName, value)}
                        />
                    </Grid>
                    <Grid item xs={4}>
                        <TextBox
                            id="conditionalAccountHolder"
                            label={'Account holder name'}
                            value={parameters.conditionalAccountHolder?.value}
                            onChange={(value) => valueChanged(parameters.conditionalAccountHolder, value)}
                        />
                    </Grid>
                    <Grid item xs={12}>
                        <Typography variant="h6" style={{marginTop: 16, marginBottom: 8}}>
                            <div className={classes.sectionHeader}>
                                Primary Account
                                {primaryValidationStatus.status === 'pass' && (
                                    <Typography className={classes.validationStatus}>
                                        Validated - Pass ({primaryValidationStatus.date} -{' '}
                                        {primaryValidationStatus.agentName})
                                    </Typography>
                                )}
                            </div>
                        </Typography>
                    </Grid>
                    <Grid item xs={4}>
                        <SelectInputElement
                            param={{
                                label: 'Account Type',
                                name: 'conditionalAccountType',
                                options: ['CHECKING', 'SAVINGS'],
                                ...parameters.conditionalAccountType,
                            }}
                            required={required('')}
                            valueChanged={(param, value) => {
                                valueChanged(param, value);
                                resetVerification();
                            }}
                        />
                    </Grid>
                    <Grid item xs={4}>
                        <TextBox
                            id="conditionalRoutingNumber"
                            label={'Routing number'}
                            value={parameters.conditionalRoutingNumber?.value}
                            onChange={(value) => {
                                valueChanged(parameters.conditionalRoutingNumber, value);
                                resetVerification();
                                // Clear validation metadata
                                valueChanged(parameters.primaryAccountValidationDate, '');
                                valueChanged(parameters.primaryAccountValidationAgent, '');

                                // Set error only if BOTH fields have values
                                if (value && parameters.conditionalAccountNumber?.value) {
                                    valueChanged(parameters.primaryAccountValidationError, 'Not validated');
                                    setPrimaryValidationStatus({
                                        status: 'fail',
                                        error: 'Not validated',
                                    });
                                } else {
                                    // Clear error if either field is empty
                                    valueChanged(parameters.primaryAccountValidationError, '');
                                    setPrimaryValidationStatus({status: null});
                                }
                            }}
                        />
                    </Grid>
                    <Grid item xs={4}>
                        <TextBox
                            id="conditionalAccountNumber"
                            label={'Account number'}
                            value={parameters.conditionalAccountNumber?.value}
                            onChange={(value) => {
                                valueChanged(parameters.conditionalAccountNumber, value);
                                resetVerification();
                                // Clear validation metadata
                                valueChanged(parameters.primaryAccountValidationDate, '');
                                valueChanged(parameters.primaryAccountValidationAgent, '');

                                // Set error only if BOTH fields have values
                                if (value && parameters.conditionalRoutingNumber?.value) {
                                    valueChanged(parameters.primaryAccountValidationError, 'Not validated');
                                    setPrimaryValidationStatus({
                                        status: 'fail',
                                        error: 'Not validated',
                                    });
                                } else {
                                    // Clear error if either field is empty
                                    valueChanged(parameters.primaryAccountValidationError, '');
                                    setPrimaryValidationStatus({status: null});
                                }
                            }}
                        />
                    </Grid>
                    <Grid item xs={12}>
                        <Button
                            variant="contained"
                            color="primary"
                            onClick={validatePrimaryAccount}
                            disabled={isPrimaryValidating}
                            className={classes.validateButton}
                        >
                            {isPrimaryValidating ? 'Validating...' : 'Validate'}
                        </Button>
                        {primaryValidationStatus.status === 'fail' && primaryValidationStatus.error && (
                            <Typography className={classes.validationError}>{primaryValidationStatus.error}</Typography>
                        )}
                    </Grid>
                    <Grid item xs={12}>
                        <Typography variant="h6" style={{marginTop: 16, marginBottom: 8}}>
                            <div className={classes.sectionHeader}>
                                Secondary Account
                                {secondaryValidationStatus.status === 'pass' && (
                                    <Typography className={classes.validationStatus}>
                                        Validated - Pass ({secondaryValidationStatus.date} -{' '}
                                        {secondaryValidationStatus.agentName})
                                    </Typography>
                                )}
                            </div>
                        </Typography>
                    </Grid>
                    <Grid item xs={4}>
                        <TextBox
                            id="secondaryRoutingNumber"
                            label={'Secondary routing number'}
                            value={parameters.secondaryRoutingNumber?.value}
                            onChange={(value) => {
                                valueChanged(parameters.secondaryRoutingNumber, value);
                                // Clear validation metadata
                                valueChanged(parameters.secondaryAccountValidationDate, '');
                                valueChanged(parameters.secondaryAccountValidationAgent, '');

                                // Set error only if BOTH fields have values
                                if (value && parameters.secondaryAccountNumber?.value) {
                                    valueChanged(parameters.secondaryAccountValidationError, 'Not validated');
                                    setSecondaryValidationStatus({
                                        status: 'fail',
                                        error: 'Not validated',
                                    });
                                } else {
                                    // Clear error if either field is empty
                                    valueChanged(parameters.secondaryAccountValidationError, '');
                                    setSecondaryValidationStatus({status: null});
                                }
                            }}
                        />
                        <small>Collect if TU_CREDIT_RATING in Hubspot = "Sub Prime"</small>
                    </Grid>
                    <Grid item xs={4}>
                        <TextBox
                            id="secondaryAccountNumber"
                            label={'Secondary account number'}
                            value={parameters.secondaryAccountNumber?.value}
                            onChange={(value) => {
                                valueChanged(parameters.secondaryAccountNumber, value);
                                // Clear validation metadata
                                valueChanged(parameters.secondaryAccountValidationDate, '');
                                valueChanged(parameters.secondaryAccountValidationAgent, '');

                                // Set error only if BOTH fields have values
                                if (value && parameters.secondaryRoutingNumber?.value) {
                                    valueChanged(parameters.secondaryAccountValidationError, 'Not validated');
                                    setSecondaryValidationStatus({
                                        status: 'fail',
                                        error: 'Not validated',
                                    });
                                } else {
                                    // Clear error if either field is empty
                                    valueChanged(parameters.secondaryAccountValidationError, '');
                                    setSecondaryValidationStatus({status: null});
                                }
                            }}
                        />
                        <small>Collect if TU_CREDIT_RATING in Hubspot = "Sub Prime"</small>
                    </Grid>
                    <Grid item xs={12}>
                        <Button
                            variant="contained"
                            color="primary"
                            onClick={validateSecondaryAccount}
                            disabled={
                                isSecondaryValidating ||
                                !parameters.secondaryRoutingNumber?.value ||
                                !parameters.secondaryAccountNumber?.value
                            }
                            className={classes.validateButton}
                        >
                            {isSecondaryValidating ? 'Validating...' : 'Validate'}
                        </Button>
                        {secondaryValidationStatus.status === 'fail' && secondaryValidationStatus.error && (
                            <Typography className={classes.validationError}>
                                {secondaryValidationStatus.error}
                            </Typography>
                        )}
                    </Grid>
                    <Grid item xs={12}>
                        <FormControlLabel
                            control={
                                <Checkbox
                                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                                        valueChanged(parameters.conditionalNextAvailable, e.target.checked);
                                        if (e.target.checked && parameters.conditionalDraftDate.value) {
                                            valueChanged(parameters.conditionalDraftDate, '');
                                        }
                                    }}
                                    checked={parameters.conditionalNextAvailable?.value}
                                    name="conditionalNextAvailable"
                                    color="primary"
                                />
                            }
                            label="Client prefers next available draft date"
                        />
                    </Grid>
                    <Grid item xs={4}>
                        <SelectInputElement
                            param={{
                                label: 'Convenient Monthly Draft Date',
                                name: 'conditionalDraftDate',
                                options: filterDraftDateOptions(
                                    fieldOptions.conditionalDraftDate.options,
                                    isFGDraftDate,
                                ),
                                ...parameters.conditionalDraftDate,
                            }}
                            disabled={!!parameters.conditionalNextAvailable.value}
                            required={required('', !parameters.conditionalNextAvailable.value)}
                            valueChanged={valueChanged}
                            error={isFGDraftDateInvalid}
                            helperText={
                                isFGDraftDateInvalid && 'For F&G products, the draft date cannot be set to 14 or 15'
                            }
                        />
                    </Grid>
                    <Grid item xs={4}>
                        <TextBox
                            id="conditionalPaymentFreq"
                            label={'Preferred payment frequency'}
                            InputProps={{
                                readOnly: true,
                            }}
                            disabled={true}
                            value={parameters.conditionalPaymentFreq?.value}
                        />
                    </Grid>
                </CategoryCard>
                <Snackbar
                    anchorOrigin={{
                        vertical: 'top',
                        horizontal: 'center',
                    }}
                    open={toastOpen}
                    autoHideDuration={5000}
                    onClose={() => setToastOpen(false)}
                >
                    <Alert elevation={6} variant="filled" severity="success" onClose={() => setToastOpen(false)}>
                        <strong>Success</strong>
                        <br />
                        {toastMessage}
                    </Alert>
                </Snackbar>
            </>
        );
    },
);

export default ConditionalCoverageBanking;
